import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Container,
  Alert,
  LinearProgress,
  Fade,
  Slide,
  Divider,
} from '@mui/material';
import {
  HourglassEmpty,
  Email,
  School,
  CheckCircleOutline,
  AssignmentTurnedIn,
  Groups,
} from '@mui/icons-material';
import { useAuth, useClubs } from '../../context/AppContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ParticleBackground from '../../components/common/ParticleBackground';

type ApplicationType = 'registration' | 'club-application';

const PendingApprovalPage: React.FC = () => {
  const { user, logout, isAuthenticated, authInitialized } = useAuth();
  const { clubs } = useClubs();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Determine application type from URL params or location state
  const [applicationType, setApplicationType] = useState<ApplicationType>('registration');
  const [clubId, setClubId] = useState<string | null>(null);
  const [clubName, setClubName] = useState<string | null>(null);

  useEffect(() => {
    // Wait for auth to initialize before making any navigation decisions
    if (!authInitialized) {
      return;
    }

    // Redirect to login if not authenticated after initialization completes
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    // Check if this is a club application from URL params
    const clubIdParam = searchParams.get('clubId');
    const typeParam = searchParams.get('type');

    // Check location state (passed from QuizPage)
    const stateClubId = (location.state as any)?.clubId;
    const stateType = (location.state as any)?.type;

    if (typeParam === 'club-application' || stateType === 'club-application') {
      setApplicationType('club-application');
      const finalClubId = clubIdParam || stateClubId;
      setClubId(finalClubId);

      // Find club name from clubs list
      if (finalClubId && clubs.length > 0) {
        const club = clubs.find(c => c.id === finalClubId);
        setClubName(club?.name || null);
      }
    } else {
      // If verified user lands here without club application context, redirect to clubs
      if (user && user.isVerified) {
        navigate('/clubs', { replace: true });
      }
    }
  }, [user, isAuthenticated, authInitialized, searchParams, location.state, clubs, navigate]);

  // Show loading while auth is initializing
  if (!authInitialized) {
    return (
      <>
        <ParticleBackground />
        <Container component="main" maxWidth="sm">
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Loading...
            </Typography>
            <LinearProgress sx={{ width: '100%', maxWidth: 400 }} />
          </Box>
        </Container>
      </>
    );
  }

  // If user is still not available after loading, don't render
  if (!user) {
    return null;
  }

  const handleLogout = () => {
    logout();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleViewClubs = () => {
    navigate('/clubs');
  };

  const handleViewApplications = () => {
    navigate('/my-applications');
  };

  const handleViewClubDetails = () => {
    if (clubId) {
      navigate(`/clubs/${clubId}`);
    }
  };

  // Render content based on application type
  const renderIcon = () => {
    if (applicationType === 'club-application') {
      return (
        <AssignmentTurnedIn
          sx={{
            fontSize: 80,
            color: 'success.main',
          }}
        />
      );
    }
    return (
      <HourglassEmpty
        sx={{
          fontSize: 80,
          color: 'warning.main',
        }}
      />
    );
  };

  const renderTitle = () => {
    if (applicationType === 'club-application') {
      return 'Application Submitted!';
    }
    return 'Account Pending Approval';
  };

  const renderMessage = () => {
    if (applicationType === 'club-application') {
      return (
        <>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 500, mx: 'auto' }}>
            Your application has been submitted successfully! The club mentor will review your quiz and you'll be notified upon approval.
          </Typography>
          {clubName && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
              <Groups color="primary" />
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                {clubName}
              </Typography>
            </Box>
          )}
        </>
      );
    }
    return (
      <>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 500, mx: 'auto' }}>
          Your account has been created successfully!
        </Typography>
        <Alert severity="success" sx={{ mb: 3, textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>✓ Email Verified</strong>
          </Typography>
          <Typography variant="body2">
            Your email has been verified successfully.
          </Typography>
        </Alert>
        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>⏳ Management Approval Pending</strong>
          </Typography>
          <Typography variant="body2">
            Please wait while faculty reviews and approves your registration.
          </Typography>
        </Alert>
      </>
    );
  };

  return (
    <>
      <ParticleBackground />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 4,
          }}
        >
        <Slide direction="down" in={true} timeout={600}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.06)',
              textAlign: 'center',
            }}
          >
            {/* Header */}
            <Fade in={true} timeout={800}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Unii Club
                </Typography>
              </Box>
            </Fade>

          {/* Icon */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {renderIcon()}
          </Box>

          {/* Main message */}
          <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
            {renderTitle()}
          </Typography>

          {renderMessage()}

          {/* User details - only for registration */}
          {user && applicationType === 'registration' && (
            <Box
              sx={{
                mb: 4,
                p: 3,
                bgcolor: 'background.default',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Account Details
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Email sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.primary">
                  {user.email}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <School sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.primary">
                  {user.branch}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleOutline sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.primary">
                  {user.role}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Progress indicator - only for registration */}
          {applicationType === 'registration' && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Approval in progress...
              </Typography>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          )}

          {/* Information alert */}
          {applicationType === 'registration' ? (
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>What happens next?</strong>
                <br />
                • Faculty from your department will review your application
                <br />
                • You'll receive access once approved
                <br />
                • This usually takes 1-2 business days
                <br />
                • You'll be notified via email once approved
              </Typography>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>What happens next?</strong>
                <br />
                • Club mentor will review your quiz results
                <br />
                • Decision made within 2-3 days
                <br />
                • You'll receive notification of approval
                <br />
                • Welcome to the club once approved!
              </Typography>
            </Alert>
          )}

          {/* Action buttons */}
          {applicationType === 'registration' ? (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                sx={{
                  minWidth: 120,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2.5,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                onClick={handleLogout}
                sx={{
                  minWidth: 120,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                Sign Out
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
              {clubId && (
                <Button
                  variant="outlined"
                  onClick={handleViewClubDetails}
                  sx={{
                    minWidth: 120,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2.5,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                    },
                  }}
                >
                  View Club Details
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleViewClubs}
                sx={{
                  minWidth: 120,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                Back to Clubs
              </Button>
            </Box>
          )}

          {/* Additional help */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {applicationType === 'registration'
                ? 'Need help? Contact your department administrator or '
                : 'Need help? Contact the club mentor or '}
              <strong>support@uniiclub.edu</strong>
            </Typography>
          </Box>
        </Paper>
        </Slide>
      </Box>
    </Container>
    </>
  );
};

export default PendingApprovalPage;